import React, { useState } from 'react'
import { Accordion, AccordionHeader, AccordionItem, AccordionPanel, Button, Card, makeStyles, tokens } from '@fluentui/react-components'
import { ArrowClockwise20Regular, Copy20Regular, ErrorCircle48Regular, Home20Regular } from '@fluentui/react-icons'
import { Text } from '../text/Text'
import { t } from '../../../i18n/i18n'
import { normalizeError, showTechnicalDetails } from '../../../helpers/errorHelper'

const useStyles = makeStyles({
   container: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '60vh',
      width: '100%',
      padding: tokens.spacingVerticalXXL,
      boxSizing: 'border-box',
   },
   card: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center',
      gap: tokens.spacingVerticalM,
      maxWidth: '560px',
      width: '100%',
      padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXXL}`,
      borderRadius: tokens.borderRadiusLarge,
      boxShadow: tokens.shadow8,
      backgroundColor: tokens.colorNeutralBackground1,
   },
   icon: {
      color: tokens.colorPaletteMarigoldForeground3,
   },
   actions: {
      display: 'flex',
      gap: tokens.spacingHorizontalM,
      marginTop: tokens.spacingVerticalM,
      flexWrap: 'wrap',
      justifyContent: 'center',
   },
   reference: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: tokens.spacingVerticalXXS,
      marginTop: tokens.spacingVerticalM,
      color: tokens.colorNeutralForeground3,
   },
   details: {
      width: '100%',
      marginTop: tokens.spacingVerticalM,
      textAlign: 'left',
   },
   detailsText: {
      fontFamily: tokens.fontFamilyMonospace,
      fontSize: tokens.fontSizeBase200,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
      maxHeight: '240px',
      overflowY: 'auto',
      margin: 0,
   },
})

type ErrorFallbackProps = {
   error: unknown
}

export const ErrorFallback = ({ error }: ErrorFallbackProps): JSX.Element => {
   const classes = useStyles()
   const [copied, setCopied] = useState(false)
   const [occurredAt] = useState(() => new Date())
   const normalized = normalizeError(error)

   const timestamp = occurredAt.toLocaleString('sv-SE')

   const copyDetails = async () => {
      const lines = [
         `${t('ErrorOccurredAt')}: ${timestamp}`,
         `URL: ${window.location.pathname}`,
         normalized.status !== undefined ? `Status: ${normalized.status}` : undefined,
         normalized.traceId ? `${t('ReferenceId')}: ${normalized.traceId}` : undefined,
         showTechnicalDetails && normalized.detail ? normalized.detail : undefined,
      ].filter((line): line is string => line !== undefined)

      try {
         await navigator.clipboard.writeText(lines.join('\n'))
         setCopied(true)
      } catch {
         // Ignore error at copying to clipboard, as this is not critical for the user experience.
      }
   }

   return (
      <div className={classes.container} role="alert">
         <Card className={classes.card}>
            <ErrorCircle48Regular className={classes.icon} />
            <Text size={500} weight="semibold" align="center">
               {t(normalized.titleKey)}
            </Text>
            <Text size={400} align="center">
               {t(normalized.messageKey)}
            </Text>

            <div className={classes.actions}>
               <Button appearance="primary" icon={<ArrowClockwise20Regular />} onClick={() => window.location.reload()}>
                  {t('ReloadPage')}
               </Button>
               <Button appearance="secondary" icon={<Home20Regular />} onClick={() => window.location.assign('/')}>
                  {t('GoToStartPage')}
               </Button>
            </div>

            <div className={classes.reference}>
               <Text size={200} align="center">
                  {t('ErrorContactSupport')}
               </Text>
               <Text size={200} align="center">
                  {`${t('ErrorOccurredAt')}: ${timestamp}`}
                  {normalized.status !== undefined ? ` · Status: ${normalized.status}` : ''}
               </Text>
               {normalized.traceId ? (
                  <Text size={200} align="center">
                     {`${t('ReferenceId')}: ${normalized.traceId}`}
                  </Text>
               ) : null}
               <Button appearance="transparent" size="small" icon={<Copy20Regular />} onClick={copyDetails}>
                  {copied ? t('CopiedToClipboard') : t('CopyErrorDetails')}
               </Button>
            </div>

            {showTechnicalDetails && normalized.detail ? (
               <Accordion collapsible className={classes.details}>
                  <AccordionItem value="details">
                     <AccordionHeader expandIconPosition="start">
                        <Text size={200}>{t('ShowTechnicalDetails')}</Text>
                     </AccordionHeader>
                     <AccordionPanel>
                        <pre className={classes.detailsText}>{normalized.detail}</pre>
                     </AccordionPanel>
                  </AccordionItem>
               </Accordion>
            ) : null}
         </Card>
      </div>
   )
}
